import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { History, ArrowLeft, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { supabase } from '../lib/supabase';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function JournalHistory() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<any[]>([]);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');
  const [textContent, setTextContent] = useState('');
  const [userTimeZone, setUserTimeZone] = useState('');

  useEffect(() => {
    async function fetchUserTimeZone() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('time_zone')
          .eq('id', session.user.id)
          .single();

        if (profile?.time_zone) {
          setUserTimeZone(profile.time_zone);
        } else {
          // Default to browser's timezone if not set in profile
          setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        }
      } catch (error) {
        console.error('Error fetching user timezone:', error);
        // Fall back to browser's timezone
        setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      }
    }

    fetchUserTimeZone();
  }, []);

  useEffect(() => {
    async function fetchDecryptedJournals() {
      try {
        setIsLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Not authenticated');

        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/decrypt-user-entries`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ type: 'journal' })
        });

        if (!response.ok) throw new Error('Failed to fetch journal entries');

        const data = await response.json();
        console.log('✅ Decrypted journal entries from Edge Function:', data);

        const fullName = session.user.user_metadata?.name || 'User';
        const updatedHeader = `📘 ${fullName}'s Daily Action Journal`;
        const cleanedContent = data.textContent
          .replace(/^.*GOLD FILE.*\n+/, `${updatedHeader}\n\n`)
          .replace(/📘 JOURNAL ENTRY\n+/g, '');

        setTextContent(cleanedContent);

        const journalArray = cleanedContent
          .replace(/^📘.*Daily Action Journal.*\n+/, '')
          .split('----------------------------------------')
          .map(section => {
            const dateMatch = section.match(/Date: (.+)/);
            const celebrateMatch = section.match(/Celebrate|Celebration: ([\s\S]*?)\n(?:Learning|Goal):/);
            const learningMatch = section.match(/Learning: ([\s\S]*?)\nGoal:/);
            const goalMatch = section.match(/Goal: ([\s\S]*?)\n(?:Goal Importance|Why It's Important):/);
            const importanceMatch = section.match(/(?:Goal Importance|Why It's Important): ([\s\S]*?)\n(?:Reflection|Awareness):/);
            const awarenessMatch = section.match(/(?:Reflection|Awareness): ([\s\S]*?)\n(?:Value )?Alignment:/);
            const alignmentMatch = section.match(/(?:Value )?Alignment: ([\s\S]*?)\nAction:/);
            const actionMatch = section.match(/Action: ([\s\S]*)/);

            return dateMatch ? {
              created_at: new Date(dateMatch[1].trim()).toISOString(),
              celebration: celebrateMatch?.[1]?.trim() || '',
              celebration_learning: learningMatch?.[1]?.trim() || '',
              goal: goalMatch?.[1]?.trim() || '',
              goal_importance: importanceMatch?.[1]?.trim() || '',
              reflection: awarenessMatch?.[1]?.trim() || '',
              value_alignment: alignmentMatch?.[1]?.trim() || '',
              action: actionMatch?.[1]?.trim() || ''
            } : null;
          })
          .filter(Boolean);

        setEntries(journalArray);
      } catch (err: any) {
        console.error('Error loading journal entries:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchDecryptedJournals();
  }, [navigate]);

  const toggleEntry = (entryId: string) => {
    setExpandedEntry(expandedEntry === entryId ? null : entryId);
  };

  const formatEntryDate = (timestamp: string) => {
    try {
      const date = parseISO(timestamp);
      // Format the date using the user's timezone
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: userTimeZone
      }).format(date);
    } catch (error) {
      console.error('Date parsing error:', error);
      return 'Invalid date';
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const fullName = session.user.user_metadata?.name || 'User';
      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Daily Action Journal - ${fullName}.txt`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download journal history');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
     <ProtectedLayout>
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <History className="w-8 h-8 text-[#236caf]" />
              <h1 className="text-3xl font-bold text-[#004060]">Action Journal History</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
              <Button
                variant="ghost"
                onClick={handleDownload}
                className="px-2 py-1 border rounded-lg"
                disabled={entries.length === 0}
              >
                <Download className="w-5 h-5 text-[#004060]" />
              </Button>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-center text-gray-500 italic">Loading journal entries...</div>
          ) : entries.length === 0 ? (
            <p className="text-gray-500 italic">No journal entries available.</p>
          ) : (
            <div className="space-y-4">
              {entries.map((entry: any) => (
                <div key={entry.created_at} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleEntry(entry.created_at)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <h2 className="text-lg font-semibold text-[#004060]">
                      {formatEntryDate(entry.created_at)}
                    </h2>
                    {expandedEntry === entry.created_at ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>

                  {expandedEntry === entry.created_at && (
                    <div className="p-4 border-t space-y-2 text-sm text-gray-700">
                      <p><strong>Celebration:</strong> {entry.celebration || 'N/A'}</p>
                      {entry.celebration_learning && (
                        <p><strong>Learning:</strong> {entry.celebration_learning}</p>
                      )}
                      <p><strong>Goal:</strong> {entry.goal || 'N/A'}</p>
                      {entry.goal_importance && (
                        <p><strong>Goal Importance:</strong> {entry.goal_importance}</p>
                      )}
                      <p><strong>Reflection:</strong> {entry.reflection || 'N/A'}</p>
                      <p><strong>Value Alignment:</strong> {entry.value_alignment || 'N/A'}</p>
                      <p><strong>Action:</strong> {entry.action || 'N/A'}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
     </ProtectedLayout>
  );
}